import { css } from "../helpers/webComponents";
import { TextActionResult } from "../../../api/src/game-base/actionResults/TextActionResult";

const styles: string = css`
    :host {
        background-color: #1D2434;
        border: 3px solid #3B4B62;
        color: white;
        display: flex;
        flex-direction: column;
        padding: 20px;
        position: absolute;
        z-index: 10;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        border-radius: 8px;
        box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.5);
        width: 300px;
    }

    .minigame-header {
        font-size: 1.2em;
        margin-bottom: 20px;
        text-align: center;
    }

    .button-container {
        display: flex;
        gap: 10px;
        justify-content: center;
        margin-top: 20px;
    }

    .game-button {
        width: 50px;
        height: 50px;
        font-size: 1.2rem;
        background: #3B4B62;
        border: none;
        border-radius: 5px;
        cursor: pointer;
        transition: background 0.2s;
        color: white;
    }

    .game-button:hover {
        background: #4F6A8B;
    }

    .correct {
        background-color: green !important;
    }

    .close-btn {
        margin-top: 20px;
        padding: 8px 16px;
        background: #e63946;
        color: white;
        border: none;
        border-radius: 5px;
        cursor: pointer;
        align-self: center;
    }
`;

/**
 * Minigame Component - Vijf knoppen in de juiste volgorde indrukken
 */
export class MiniGameComponent extends HTMLElement {
    private correctSequence: string[] = [];
    private playerSequence: string[] = [];
    private isOpen: boolean = false;
    private onCompletedCallback?: () => void;

    public constructor() {
        super();
        this.attachShadow({ mode: "open" });
        this.render();
    }

    public connectedCallback(): void {
        this.style.display = "none";

        // Listen for keyboard events to toggle visibility with "z" key
        document.addEventListener("keypress", this.handleKeyPress.bind(this));
    }

    public disconnectedCallback(): void {
        document.removeEventListener("keypress", this.handleKeyPress.bind(this));
    }

    /**
     * Handle key press events for toggling the mini-game
     */
    private handleKeyPress(event: KeyboardEvent): void {
        if (event.key === "z") {
            this.toggle();
        }
    }

    /**
     * Set a callback function to be called when the mini-game is completed
     */
    public setCompletedCallback(callback: () => void): void {
        this.onCompletedCallback = callback;
    }

    /**
     * Toggle the visibility of the minigame
     */
    public toggle(): void {
        this.isOpen = !this.isOpen;
        this.style.display = this.isOpen ? "flex" : "none";

        if (this.isOpen) {
            this.startGame();
        }
    }

    /**
     * Start de minigame
     */
    public startGame(): void {
        this.correctSequence = this.generateRandomSequence();
        this.playerSequence = [];
        this.render();
    }

    /**
     * Maak random volgorde van knoppen
     */
    private generateRandomSequence(): string[] {
        const glyphs: string[] = ["Σ", "ξ", "Φ", "Ω", "Ψ"];
        return glyphs.sort(() => Math.random() - 0.5);
    }

    /**
     * Controleert of de speler de juiste volgorde volgt
     */
    private async handleButtonClick(value: string): Promise<void> {
        const button: Element | null | undefined = this.shadowRoot?.querySelector(`.game-button[data-value="${value}"]`);
        this.playerSequence.push(value);

        if (this.playerSequence[this.playerSequence.length - 1] !== this.correctSequence[this.playerSequence.length - 1]) {
            this.playerSequence = [];
            alert("Wrong order! Try again.");

            // Reset the buttons
            this.shadowRoot?.querySelectorAll(".game-button").forEach(btn => btn.classList.remove("correct"));
            return;
        }

        if (button) {
            button.classList.add("correct");
        }

        if (this.playerSequence.length === 5) {
            const result = await this.completeMinigame();
            alert("You placed the glyphs in the correct order!");

            if (this.onCompletedCallback) {
                this.onCompletedCallback();
            }
            location.reload();
            this.closeGame();
        }
    }

    /**
    * Handle minigame completion and API request
    */
    private async completeMinigame(): Promise< TextActionResult | undefined> {
        try {
            const response: Response = await fetch("http://localhost:3001/set/minigame/complete", {
                method: "POST",
                body: JSON.stringify({ succes: true }), // Note: backend checks for 'succes', not 'success'
                headers: {
                    "Content-Type": "application/json",
                },
            });

            return new TextActionResult(["Minigame voltooid! De deur opent..."]);
        }
        catch (error) {
            console.error("Fout bij het opslaan van minigame status:", error);
            return new TextActionResult(["Fout bij het opslaan van minigame status."]);
        }
    }

    /**
     * Sluit de minigame
     */
    private closeGame(): void {
        this.isOpen = false;
        this.style.display = "none";
    }

    /**
     * Render de component in de shadow DOM
     */
    private render(): void {
        if (!this.shadowRoot) return;

        this.shadowRoot.innerHTML = `
            <style>${styles}</style>
            <div class="minigame-header">Press the hyroglyphs in the correct order</div>
            <div class="button-container">
                ${["Σ", "ξ", "Φ", "Ω", "Ψ"].map(glyph => `
                    <button class="game-button" data-value="${glyph}">${glyph}</button>
                `).join("")}
            </div>
            <button class="close-btn">close</button>
        `;

        this.shadowRoot.querySelectorAll(".game-button").forEach(button => {
            button.addEventListener("click", event => {
                const value: string = (event.target as HTMLElement).getAttribute("data-value") || "";
                this.handleButtonClick(value);
            });
        });

        this.shadowRoot.querySelector(".close-btn")?.addEventListener("click", () => this.closeGame());
    }
}
